import { useState } from "react";
import { useTheme } from "styled-components";
import {
  EIGHT_SYMBOLS_REGEX,
  LOWER_CASE_REGEX,
  NUMBER_REGEX,
  PASSWORD_REGEX,
  SPECIAL_SYMBOL_REGEX,
  UPPER_CASE_REGEX,
} from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const usePasswordSection = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const appTheme = useTheme();
  const [password, setPassword] = useState<string>();
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);

  const isPasswordValid = (password?: string) =>
    password && checkPassword(password);
  const validatePassword = (argsPassword?: string) => {
    const isValid = isPasswordValid(argsPassword || password);
    if (isValid) {
      setShowPasswordValidation(false);
    } else {
      setShowPasswordValidation(true);
    }
    return isValid;
  };

  const onPasswordChange = (password?: string) => {
    setPassword(password);
    return validatePassword(password);
  };

  const checkPassword = (password: string) => {
    return PASSWORD_REGEX.test(password);
  };

  const getValidationItem = (text: string, isValid: boolean) => ({
    style: { marginBottom: 8 },
    isValid,
    color: isValid ? appTheme.colors.green : appTheme.colors.red,
    text,
  });

  const validationData = Object.values(PasswordCondition).map((condition) => {
    const isValid =
      !!password && PasswordConditionToRegex[condition].test(password);
    return getValidationItem(t(condition), isValid);
  });
  if (password && password.length >= 100) {
    validationData.push(
      getValidationItem(t("password_validation.less_than_100"), false)
    );
  }

  return {
    password,
    validationTitle: t("password_validation.title"),
    validationData,
    onBlur: () => setShowPasswordValidation(false),
    onFocus: () => validatePassword(),
    validatePassword,
    onPasswordChange,
    showPasswordValidation,
    isPasswordValid: isPasswordValid(password),
  };
};

export default usePasswordSection;

export interface ValidationItemData {
  style: any;
  isValid: boolean;
  color: string;
  text: string;
}

enum PasswordCondition {
  NUM_OF_SYMBOLS = "password_validation.eight_characters",
  SPECIAL_SYMBOL = "password_validation.special_character",
  UPPER_CASE = "password_validation.uppercase_character",
  LOWER_CASE = "password_validation.lowercase_character",
  NUMBER = "password_validation.number",
}

const PasswordConditionToRegex = {
  [PasswordCondition.NUM_OF_SYMBOLS]: EIGHT_SYMBOLS_REGEX,
  [PasswordCondition.SPECIAL_SYMBOL]: SPECIAL_SYMBOL_REGEX,
  [PasswordCondition.UPPER_CASE]: UPPER_CASE_REGEX,
  [PasswordCondition.LOWER_CASE]: LOWER_CASE_REGEX,
  [PasswordCondition.NUMBER]: NUMBER_REGEX,
};
